import type { MeditationType } from "~/features/meditations/types";

import { isNoteInteractionType } from "./meditation-catalog";

// AIDEV-NOTE: Minimal part-interaction shape this resolver needs. We avoid
// importing the heavy PartInteractionWithExtras type so the helper stays
// usable from any caller (public/anon included).
export interface PartInteractionForPlayback {
    id: string;
    createdAt: string | Date;
    session?: { meetingStartTime?: string | Date | null } | null;
    // AIDEV-NOTE: Rows whose `type` is a note KIND (journal/biography/youtube) are skipped
    // when picking the interaction that anchors a check-in — they carry no part context.
    // A noteId alone is not the signal: parts created from notes/chat carry real types.
    type?: string | null;
}

// AIDEV-NOTE: Minimal meditation shape — works for both TMeditation (part page,
// no signedUrl) and TSignedMeditation. signedUrl is optional and only used to
// prefer playable rows when present.
export interface MeditationForPlayback {
    id: string;
    // AIDEV-NOTE: Accepts the full DB row union ("exercise" included) — the
    // resolver itself filters to type === "part" below.
    type: MeditationType | "exercise";
    status: string;
    createdAt: string | Date;
    signedUrl?: string | null;
}

export interface ResolvePartMeditationPlaybackArgs {
    interactions: PartInteractionForPlayback[];
    meditations: MeditationForPlayback[];
    // AIDEV-NOTE: Public sessions are cache-only (no generation) so they must
    // pass a meditationId to stream a pre-cached file. Authenticated users rely
    // on the voice-keyed storage-path cache check, so they pass no meditationId.
    isPublicSession: boolean;
}

export interface PartMeditationPlayback {
    // AIDEV-NOTE: Latest interaction id keys both generation and the voice-keyed
    // cache. Selecting the latest makes "newer interaction than the stored
    // meditation → regenerate with full history" fall out automatically.
    partInteractionId?: string;
    meditationId?: string;
    hasMeditation: boolean;
}

// AIDEV-NOTE: Shape for the cached-only lookup below — same rows as
// MeditationForPlayback plus the interaction they belong to.
export interface MeditationForPartLookup extends MeditationForPlayback {
    partInteractionId?: string | null;
}

export interface ResolveCachedPartMeditationIdArgs {
    meditations: MeditationForPartLookup[];
    partInteractionId: string;
}

const toTime = (value: string | Date | null | undefined): number => {
    if (!value) return 0;
    const ms = new Date(value).getTime();
    return Number.isNaN(ms) ? 0 : ms;
};

// AIDEV-NOTE: Same date precedence as part-interactions-list.tsx getInteractionDate.
const interactionTime = (interaction: PartInteractionForPlayback): number =>
    toTime(interaction.session?.meetingStartTime ?? interaction.createdAt);

/**
 * Centralized source of truth for how a part's "Play" button should behave.
 *
 * - Picks the latest part interaction (by session.meetingStartTime, falling
 *   back to createdAt) — `part_interactions` from the repository is NOT
 *   guaranteed to be ordered, so we sort explicitly here.
 * - Picks the latest COMPLETED part meditation.
 * - Authenticated users get NO meditationId so the streaming endpoint runs its
 *   voice-keyed cache check (same voice + same latest interaction → cache hit;
 *   different voice OR newer interaction → cache miss → regenerate).
 * - Public sessions are cache-only: they get the latest COMPLETED meditationId.
 */
export function resolvePartMeditationPlayback({
    interactions,
    meditations,
    isPublicSession,
}: ResolvePartMeditationPlaybackArgs): PartMeditationPlayback {
    const partInteractions = interactions.filter(
        (interaction) => !isNoteInteractionType(interaction.type),
    );
    const latestInteraction = (
        partInteractions.length > 0 ? partInteractions : interactions
    ).reduce<PartInteractionForPlayback | undefined>((latest, current) => {
        if (!latest) return current;
        return interactionTime(current) >= interactionTime(latest)
            ? current
            : latest;
    }, undefined);

    const latestCompleted = meditations
        .filter((m) => m.type === "part" && m.status === "COMPLETED")
        .reduce<MeditationForPlayback | undefined>((latest, current) => {
            if (!latest) return current;
            return toTime(current.createdAt) >= toTime(latest.createdAt)
                ? current
                : latest;
        }, undefined);

    return {
        partInteractionId: latestInteraction?.id,
        meditationId:
            isPublicSession && latestCompleted ? latestCompleted.id : undefined,
        hasMeditation: !!latestCompleted,
    };
}

/**
 * Latest COMPLETED part meditation for one interaction, or `undefined`.
 *
 * AIDEV-NOTE: Token-shared (share-link) views are cache-only — the server refuses to
 * generate for them (see features/meditations/server/public-access.server.ts). The UI must
 * therefore resolve an EXISTING meditation id up front and either offer playback of that
 * id or render no affordance at all; a Play button with no id would only ever 404.
 */
export function resolveCachedPartMeditationId({
    meditations,
    partInteractionId,
}: ResolveCachedPartMeditationIdArgs): string | undefined {
    return meditations
        .filter(
            (m) =>
                m.type === "part" &&
                m.status === "COMPLETED" &&
                m.partInteractionId === partInteractionId,
        )
        .reduce<MeditationForPartLookup | undefined>((latest, current) => {
            if (!latest) return current;
            return toTime(current.createdAt) >= toTime(latest.createdAt)
                ? current
                : latest;
        }, undefined)?.id;
}
